// React and hooks
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '@store/index'
import {
  fetchPatientById,
  searchPatientsByName,
} from '@services/api/patient.api'
// Third-party libraries
import moment from 'moment'
import { Col, Dropdown, Form, Row } from 'react-bootstrap'

// Custom components
import { Button, InputField, Calendar } from '@components/common'
import { AppointmentFormData } from './Appointment.modal'

// Constants and utilities
import {
  DAY_OF_WEEK_LETTER,
  DURATION_OPTIONS,
  VISIT_EVERY,
} from './Appointment.constant'
import './Appointment.css'
// Relative imports
import { getAllAvailableSlots } from '@services/api/appointment.api'
import {
  IAppointmentType,
  IOperatory,
  PatientWithInsurance,
} from '@dataTypes/appointment.type'

import WorkingDayBadge from '@components/common/WorkingDayBadge/WorkingDayBadge.component'
import { Provider } from '@dataTypes/provider.types'
import { providerRoles } from '@utils/provider.util'

import { logger } from '@utils/safeLogger'

export type AvailableStartTimes = {
  ready: boolean
  times: string[]
}

type SchedulingProps = {
  formData: Partial<AppointmentFormData>
  handleChange: (
    field: string,
    value: string | number | Date | boolean | any,
  ) => void
  validationErrors: { [key: string]: string }
  preSelectedPatient?: PatientWithInsurance
  currentApptId?: string
  onAvailableStartTimesChange?: (availability: AvailableStartTimes) => void
}

type Slot = {
  start_time: string
  end_time: string
  procedure_time: string
}

const toSlotClockTime = (value?: string): string => {
  const parsed = moment(value, ['HH:mm:ss', 'HH:mm'], true)
  return parsed.isValid() ? parsed.format('HH:mm') : ''
}

const Scheduling: React.FC<SchedulingProps> = ({
  formData,
  handleChange,
  validationErrors,
  preSelectedPatient,
  currentApptId,
  onAvailableStartTimesChange,
}) => {
  const [calendar1, setCalendar1] = useState(false)
  const [calendar2, setCalendar2] = useState(false)
  const [patients, setPatients] = useState<PatientWithInsurance[]>([])
  const [loading, setLoading] = useState(false)
  const [isDurationDisable, setIsDurationDisable] = useState<boolean>(true)
  const [slots, setSlots] = useState<Slot[]>([])
  const [slotsReady, setSlotsReady] = useState(false)
  const slotsRequestRef = useRef(0)
  const [debouncedName, setDebouncedName] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedPatient, setSelectedPatient] =
    useState<PatientWithInsurance | null>(null)
  const recurringDays = useRef<number[]>(
    formData?.recurringDays
      ? formData.recurringDays.split(',').map(Number)
      : [],
  )

  const operatoryList = useSelector((state: RootState) => state.operatory.data)
  const providerList = useSelector((state: RootState) =>
    state.provider.data.filter((provider) =>
      providerRoles.includes(provider.work_role ?? ''),
    ),
  )
  const appointmentTypeList = useSelector(
    (state: RootState) => state.appointmentType.data,
  )

  const closeDropdown = (): void => {
    setShowDropdown(false) // Close the dropdown
  }

  const toggleCalendar1 = (): void => {
    setCalendar1(!calendar1)
  }

  const toggleCalendar2 = (): void => {
    setCalendar2(!calendar2)
  }

  const handleRecurringDaysClick = (dayValue: number): void => {
    if (!recurringDays.current.includes(dayValue)) {
      recurringDays.current.push(dayValue)
    } else {
      recurringDays.current = recurringDays.current.filter(
        (i) => i !== dayValue,
      )
    }

    recurringDays.current = recurringDays.current.sort((a, b) => a - b)

    handleChange('recurringDays', recurringDays.current.join(','))
  }

  const renderRecurringDays = (): JSX.Element => {
    return (
      <div className="working-days-container">
        {DAY_OF_WEEK_LETTER.map((day, index) => (
          <WorkingDayBadge
            key={index}
            day={day}
            onClick={() => handleRecurringDaysClick(index)}
            isAvailable={recurringDays.current.includes(index)}
          />
        ))}
      </div>
    )
  }

  // Handle input change and update debounce term
  const patientNameHandleChange = (value: string): void => {
    handleChange('patientName', value)
    setDebouncedName(value)
  }

  const fetchPatients = async (name: string): Promise<void> => {
    try {
      setLoading(true)
      const response = await searchPatientsByName(name) // Call the API
      if (response.statusCode === 200) {
        setPatients(response.data?.patients)
      } else {
        setPatients([])
      }
    } catch (error) {
      logger.error('Error fetching patients:', error)
      setPatients([])
    } finally {
      setLoading(false)
    }
  }

  const fetchAvailableSlots = useCallback(async (): Promise<void> => {
    if (
      !formData.providerId ||
      !formData.operatoryId ||
      !formData.apptDate ||
      !formData.duration
    ) {
      setSlots([])
      setSlotsReady(false)
      return
    }

    const requestId = ++slotsRequestRef.current
    setSlotsReady(false)
    try {
      const response = await getAllAvailableSlots({
        provider_id: formData.providerId,
        operatory_id: formData.operatoryId,
        date: moment(formData.apptDate).format('YYYY-MM-DD'),
        duration: Number(formData.duration),
        ...(formData.patientId ? { patient_id: formData.patientId } : {}),
        ...(currentApptId && {
          exclude_appointment_id: currentApptId,
        }),
      })

      if (requestId !== slotsRequestRef.current) return

      if (response.statusCode !== 200) {
        setSlots([])
        setSlotsReady(true)
        return
      }

      setSlots(response.data.slots ?? [])
      setSlotsReady(true)
    } catch (error: any) {
      if (requestId !== slotsRequestRef.current) return
      logger.error('Error fetching available slots:', error)
      setSlots([])
      setSlotsReady(true)
    }
  }, [
    formData.providerId,
    formData.patientId,
    formData.operatoryId,
    formData.apptDate,
    formData.duration,
    currentApptId,
  ])

  // Handle patient selection from the dropdown
  const handlePatientSelect = (patient: PatientWithInsurance): void => {
    setSelectedPatient(patient) // optional, for local display
    handleChange('patientName', `${patient.first_name} ${patient.last_name}`)
    handleChange('patientId', patient.patient_id)
    handleChange('insuranceOptions', patient.insurance_plans ?? [])
    setPatients([]) // hide dropdown
  }

  const handleProviderSelect = (id: string): void => {
    const selectedProvider = providerList.find(
      (prov: Provider) => prov.provider_id === id,
    )
    if (!selectedProvider) return
    // Set the provider details in the form data
    handleChange('providerId', selectedProvider.provider_id)
    handleChange(
      'providerName',
      `${selectedProvider.first_name} ${selectedProvider.last_name}`,
    )
  }

  const handleOperatorySelect = (id: string): void => {
    const selectedOP = operatoryList.find(
      (prov: IOperatory) => prov.operatory_id === id,
    )
    if (!selectedOP) return
    // Set the provider details in the form data
    handleChange('operatoryId', selectedOP.operatory_id)
    handleChange('operatoryName', `${selectedOP.name}`)
  }

  const handleTime = (slot: Slot): void => {
    handleChange('apptStartTime', slot.start_time)
    handleChange('apptEndTime', slot.end_time)
  }

  const handleAppointmentTypeSelect = (id: string): void => {
    const appointmentType = appointmentTypeList.find(
      (type: IAppointmentType) => type.appointment_type_id === id,
    )
    handleChange('appointmentTypeId', id)
    handleChange('appointmentTypeName', appointmentType?.name)
  }

  const handleAppointmentTypeSubCategorySelect = (id: string): void => {
    handleChange('appointmentTypeSubCategoryId', id)
  }

  const handleInsuranceSelect = (id: string): void => {
    const insurance = (formData.insuranceOptions ?? []).find(
      (ins) => ins.insurance_plan_id === id,
    )

    handleChange('insuranceId', id)
    handleChange('insuranceName', insurance?.plan_name ?? '')
  }

  const loadPatientDetails = async (patientId: string): Promise<void> => {
    try {
      const response = await fetchPatientById(patientId)
      if (response.statusCode === 200 && response.data?.patient) {
        const patient = response.data.patient
        handlePatientSelect(patient)
      }
    } catch (error) {
      logger.error('Failed to fetch patient by ID:', error)
    }
  }

  // Debounce the API call to reduce requests
  useEffect(() => {
    const timer = setTimeout(() => {
      if (debouncedName.trim()) {
        fetchPatients(debouncedName.trim())
      } else {
        setPatients([])
      }
    }, 300) // Adjust debounce time as needed (e.g., 300ms)

    return () => clearTimeout(timer)
  }, [debouncedName])

  useEffect(() => {
    onAvailableStartTimesChange?.({
      ready: slotsReady,
      times: slots
        .map((slot) => toSlotClockTime(slot.start_time))
        .filter((time) => time !== ''),
    })
  }, [onAvailableStartTimesChange, slots, slotsReady])

  // Fetch available slots when the form data changes
  useEffect(() => {
    fetchAvailableSlots()
  }, [
    formData.providerId,
    formData.patientId,
    formData.operatoryId,
    formData.apptDate,
    formData.duration,
    fetchAvailableSlots,
  ])

  useEffect(() => {
    if (!formData.providerId || !formData.operatoryId || !formData.apptDate) {
      setIsDurationDisable(true)
    } else {
      setIsDurationDisable(false)
    }
  }, [formData.providerId, formData.operatoryId, formData.apptDate])

  useEffect(() => {
    let patientId = ''
    if (currentApptId && formData.patientId) {
      patientId = formData.patientId
    } else if (preSelectedPatient) {
      patientId = preSelectedPatient.patient_id
    } else if (formData.patientId && !selectedPatient) {
      patientId = formData.patientId
    }
    if (patientId) {
      void loadPatientDetails(patientId)
    }
  }, [])

  const isRecurringDisabled = (): boolean => {
    if (formData.patientId) {
      const dateTimeNow = new Date()
      const apptDate = new Date(formData.apptDate!)
      const apptStartTime = formData.apptStartTime
      const [hours, minutes] = apptStartTime!.split(':').map(Number)
      apptDate.setHours(hours)
      apptDate.setMinutes(minutes)
      if (apptDate < dateTimeNow) {
        return true
      }
    }
    return false
  }

  const parsedStart = moment(formData.apptStartTime, 'HH:mm')
  return (
    <div className="appt-modal mt-2">
      <Row className="mb-2 gx-2">
        <Col md={6} className="patient-search-container">
          <InputField
            label="Name"
            value={formData.patientName || ''}
            placeholder="Search patient"
            onChange={(e) => patientNameHandleChange(e.target.value)}
            hint={validationErrors.patientName || undefined}
            destructive={!!validationErrors.patientName}
            className={
              validationErrors.patientId ? 'select-field-destructive' : ''
            }
            disabled={!!preSelectedPatient || !!currentApptId}
          />
          {validationErrors.patientId && (
            <span className="error-text">{validationErrors.patientId}</span>
          )}
          {patients.length > 0 && (
            <Col md={6}>
              <ul className="patient-dd-list">
                {patients.map((patient) => (
                  <li
                    key={patient.patient_id}
                    onClick={() => loadPatientDetails(patient.patient_id)}
                    className="patient-dropdown-item"
                  >
                    {patient.first_name} {patient.last_name}
                  </li>
                ))}
              </ul>
            </Col>
          )}
        </Col>
        <Col md="6">
          <Form.Group>
            <Form.Label>Provider</Form.Label>
            <Form.Select
              value={formData.providerId || ''}
              onChange={(e) => handleProviderSelect(e.target.value)}
              className={
                validationErrors.providerId ? 'select-field-destructive' : ''
              }
            >
              <option value={''}>{'Select'}</option>
              {providerList.map((option) => (
                <option key={option.provider_id} value={option.provider_id}>
                  {`Dr. ${option.first_name} ${option.last_name}`}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          {validationErrors.providerId && (
            <span className="error-text">{validationErrors.providerId}</span>
          )}
        </Col>
      </Row>
      <Row className="mb-2 gx-2">
        <Col md="6">
          <Form.Group controlId="formOperatoryRoom">
            <Form.Label>Operatory name</Form.Label>
            <Form.Select
              value={formData.operatoryId || ''}
              onChange={(e) => handleOperatorySelect(e.target.value)}
              className={
                validationErrors.operatoryId ? 'select-field-destructive' : ''
              }
            >
              <option value={''}>{'Select'}</option>
              {operatoryList.map((pr) => (
                <option key={pr.operatory_id} value={pr.operatory_id}>
                  {`${pr.name}`}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          {validationErrors.operatoryId && (
            <span className="error-text">{validationErrors.operatoryId}</span>
          )}
        </Col>
        <Col md="6">
          <Form.Group controlId="formDate">
            <InputField
              label="Date"
              type="text"
              value={formData.apptDate || ''}
              className="form-label-normal"
              rightIcon={
                <i
                  className="ti ti-calendar-month"
                  onClick={toggleCalendar1}
                ></i>
              }
              onChange={() => {}}
              hint={validationErrors.dateValue || undefined}
              destructive={!!validationErrors.dateValue}
            />
            {calendar1 && (
              <Calendar
                value={formData.apptDate || ''}
                onChange={(e: { value: Date }) => {
                  handleChange('apptDate', moment(e.value).format('MM/DD/YYYY'))
                  toggleCalendar1()
                }}
                // min={new Date()}
              ></Calendar>
            )}
            {calendar2 && (
              <Calendar
                value={formData.recurringEndDate || ''}
                onChange={(e: { value: Date }) => {
                  handleChange(
                    'recurringEndDate',
                    moment(e.value).format('MM/DD/YYYY'),
                  )
                  toggleCalendar2()
                }}
                min={new Date()}
              ></Calendar>
            )}
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-2 gx-2">
        <Col md="6">
          <Form.Group>
            <Form.Label>Duration</Form.Label>
            <Form.Select
              value={formData.duration || ''}
              onChange={(e) => handleChange('duration', e.target.value)}
              className={
                validationErrors.duration ? 'select-field-destructive' : ''
              }
            >
              <option value={''}>{'Select'}</option>
              {DURATION_OPTIONS.map(({ duration, text }) => (
                <option key={duration} value={duration}>
                  {text}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          {validationErrors.duration && (
            <span className="error-text">{validationErrors.duration}</span>
          )}
        </Col>
        <Col md="6">
          <Form.Group>
            <Form.Label>Time</Form.Label>
            <Dropdown
              className="duration-select"
              id="duration-select"
              show={showDropdown}
              onToggle={(isOpen) => setShowDropdown(isOpen)}
            >
              <Dropdown.Toggle
                variant={''}
                disabled={isDurationDisable}
                className={
                  validationErrors.apptStartTime
                    ? 'select-field-destructive'
                    : ''
                }
              >
                {parsedStart.isValid() ? parsedStart.format('h:mm A') : ''}
              </Dropdown.Toggle>
              <Dropdown.Menu
                className="p-3 duration-select"
                style={{ width: '390px' }}
              >
                {slots.length === 0 && (
                  <div className="no-available-slot">
                    No time slots available today
                  </div>
                )}
                {slots.length > 0 && (
                  <div className="chip-main-container">
                    {slots.map((slot) => {
                      return (
                        <Button
                          key={slot.start_time}
                          className="button-secondary chip-container"
                          onClick={() => {
                            handleTime(slot)
                            closeDropdown()
                          }}
                          size="md"
                        >
                          {moment(slot.start_time, 'HH:mm').format('h:mm A')}
                        </Button>
                      )
                    })}
                  </div>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>
          {validationErrors.apptStartTime && (
            <span className="error-text">{validationErrors.apptStartTime}</span>
          )}
        </Col>
      </Row>
      <Row className="mb-2 gx-2">
        <Col md="6">
          <Form.Group>
            <Form.Label>Appointment type</Form.Label>
            <Form.Select
              value={formData.appointmentTypeId}
              onChange={(e) => {
                handleAppointmentTypeSelect(e.target.value)
              }}
              className={
                validationErrors.appointmentTypeId
                  ? 'select-field-destructive'
                  : ''
              }
            >
              <option value={''}>{'Select'}</option>
              {appointmentTypeList.map((option) => (
                <option
                  key={option.appointment_type_id}
                  value={option.appointment_type_id}
                >
                  {option.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          {validationErrors.appointmentTypeId && (
            <span className="error-text">
              {validationErrors.appointmentTypeId}
            </span>
          )}
        </Col>
        <Col md="6">
          <Form.Group>
            <Form.Label>Sub category</Form.Label>
            <Form.Select
              value={formData.appointmentTypeSubCategoryId}
              onChange={(e) => {
                handleAppointmentTypeSubCategorySelect(e.target.value)
              }}
              className={
                validationErrors.appointmentTypeSubCategoryId
                  ? 'select-field-destructive appt-sub-cat'
                  : 'appt-sub-cat'
              }
              disabled={formData.appointmentTypeId === ''}
            >
              <option value={''}>{'Select'}</option>
              {(
                appointmentTypeList?.find(
                  (x: IAppointmentType) =>
                    x.appointment_type_id === formData.appointmentTypeId,
                ) || {}
              ).appointmenttypesubcategories?.map((option) => (
                <option
                  key={option.appointment_type_subcategory_id}
                  value={option.appointment_type_subcategory_id}
                >
                  {option.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          {validationErrors.appointmentTypeSubCategoryId && (
            <span className="error-text">
              {validationErrors.appointmentTypeSubCategoryId}
            </span>
          )}
        </Col>
      </Row>
      <Row className="mb-2 gx-2">
        <Col md={6}>
          <Form.Group controlId="formRecurring">
            <Form.Check
              type="checkbox"
              label="Recurring visit"
              checked={formData.isRecurring}
              onChange={(e) => handleChange('isRecurring', e.target.checked)}
              disabled={isRecurringDisabled()}
            />
          </Form.Group>
        </Col>
      </Row>
      {formData.isRecurring && (
        <>
          <Row className="mb-2 gx-2">
            <Col md="6">
              <Form.Group controlId="formVisitEvery">
                <Form.Label>Recurring every</Form.Label>
                <Form.Select
                  value={formData.recurringEvery}
                  onChange={(e) =>
                    handleChange('recurringEvery', e.target.value)
                  }
                >
                  {VISIT_EVERY.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md="6">
              <Form.Group controlId="formVisitEvery">
                <Form.Label>For</Form.Label>
                <div className="numeric-input-container">
                  <Form.Control
                    type="number"
                    value={formData.recurringFor}
                    min="1"
                    max="100"
                    onChange={(e) => {
                      handleChange('recurringFor', e.target.value)
                    }}
                  />
                  <span className="week-label">
                    {formData.recurringEvery?.toString() === '0'
                      ? 'Week(s)'
                      : 'Month(s)'}
                  </span>
                </div>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-2 gx-2">
            <Col md="6">
              <Form.Group controlId="formRecurringDays">
                <Form.Label>Recurring days</Form.Label>
                {renderRecurringDays()}
              </Form.Group>
            </Col>
            <Col md="6">
              <Form.Group controlId="formRecurringEndDate">
                <InputField
                  label="End date"
                  type="text"
                  value={formData.recurringEndDate || ''}
                  className="form-label-normal"
                  rightIcon={
                    <i
                      className="ti ti-calendar-month"
                      onClick={toggleCalendar2}
                    ></i>
                  }
                  onChange={() => {}}
                  hint={validationErrors.dateValue || undefined}
                  destructive={!!validationErrors.dateValue}
                />
              </Form.Group>
            </Col>
          </Row>
        </>
      )}
      <Row className="mb-2 gx-2">
        <Col md="6">
          <Form.Group>
            <Form.Label>Is insured?</Form.Label>
            {['Yes', 'No'].map((label) => (
              <Form.Check
                inline
                key={label}
                type={'radio'}
                id={''}
                label={label}
                name="is-insured"
                checked={formData.isInsured === label}
                value={label}
                onChange={(e) => handleChange('isInsured', e.target.value)}
              />
            ))}
          </Form.Group>
        </Col>
        <Col md="6">
          {formData.isInsured === 'Yes' && (
            <Form.Group controlId="formInsuranceProvider">
              <Form.Label>Insurance provider</Form.Label>
              <Form.Select
                value={formData.insuranceId}
                onChange={(e) => handleInsuranceSelect(e.target.value)}
              >
                <option value={''}>Select</option>
                {(formData.insuranceOptions ?? []).map((insurance) => (
                  <option
                    key={insurance.insurance_plan_id}
                    value={insurance.insurance_plan_id!}
                  >
                    {insurance?.carrier?.carrier_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
          {formData.isInsured === 'Yes' && validationErrors.insuranceId && (
            <span className="error-text">{validationErrors.insuranceId}</span>
          )}
        </Col>
      </Row>
    </div>
  )
}

export default Scheduling
